import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Card } from "@/components/app/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";

export const SubscriptionCard = ({ sub, plan, workspaceId, canManage, onDone }) => {
  const { L, lang } = useApp();
  const [busy, setBusy] = useState(false);
  if (!sub || sub.status === "canceled") return null;
  const end = sub.current_period_end ? new Date(sub.current_period_end * 1000).toLocaleDateString(lang === "en" ? "en-MY" : "ms-MY") : null;
  const toggle = async () => {
    setBusy(true);
    try {
      await api.post("/payments/cancel-subscription", { workspace_id: workspaceId, resume: sub.cancel_at_period_end });
      toast.success(sub.cancel_at_period_end ? L("Langganan diteruskan", "Subscription resumed") : L("Langganan akan tamat pada akhir tempoh", "Subscription will end at period end"));
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Card className="mt-6 flex flex-wrap items-center justify-between gap-3 p-5" data-testid="subscription-card">
      <div>
        <p className="font-semibold">{sub.source === "paypal" ? L("Langganan PayPal", "PayPal subscription") : L("Langganan Stripe", "Stripe subscription")}: {plan.name} · {sub.cycle === "yearly" ? L("Tahunan", "Yearly") : L("Bulanan", "Monthly")}</p>
        <p className="mt-1 text-sm text-slate-500" data-testid="subscription-status">
          {sub.cancel_at_period_end ? L(`Dibatalkan — aktif sehingga ${end || "akhir tempoh"}, kemudian kembali ke Percuma.`, `Cancelled — active until ${end || "period end"}, then reverts to Free.`)
            : sub.status === "past_due" ? L("Bayaran tertunggak — sila kemas kini kad anda.", "Payment past due — please update your card.")
              : L(`Aktif · diperbaharui automatik${end ? ` pada ${end}` : ""}`, `Active · auto-renews${end ? ` on ${end}` : ""}`)}
        </p>
      </div>
      {canManage && sub.cancel_at_period_end && sub.source !== "paypal" && <Button variant="outline" disabled={busy} onClick={toggle} className="rounded-xl" data-testid="subscription-toggle-btn">{L("Teruskan langganan", "Resume subscription")}</Button>}
      {canManage && !sub.cancel_at_period_end && (
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="outline" disabled={busy} className="rounded-xl text-red-600" data-testid="subscription-toggle-btn">{L("Batal langganan", "Cancel subscription")}</Button></AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{L("Batal langganan?", "Cancel subscription?")}</AlertDialogTitle>
              <AlertDialogDescription>{L(`Pelan ${plan.name} kekal aktif sehingga ${end || "akhir tempoh"}, kemudian kembali ke Percuma. Tiada caj lagi selepas itu.`, `${plan.name} stays active until ${end || "period end"}, then reverts to Free. No further charges.`)}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="subscription-cancel-dismiss">{L("Kekalkan", "Keep")}</AlertDialogCancel>
              <AlertDialogAction onClick={toggle} className="bg-red-600 hover:bg-red-700" data-testid="subscription-cancel-confirm">{L("Ya, batal", "Yes, cancel")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Card>
  );
};
